/**
 * Rule Progress Card Component
 * Shows progress for a single phonics rule
 */
import { PhonicsRule } from '../../data/natural-phonics';
import { RuleProgress } from '../../services/phonics-progress';

interface RuleProgressCardProps {
  rule: PhonicsRule;
  progress?: RuleProgress;
  isUnlocked: boolean;
  onClick: () => void;
  isSelected?: boolean;
}

export default function RuleProgressCard({
  rule,
  progress,
  isUnlocked,
  onClick,
  isSelected = false,
}: RuleProgressCardProps) {
  const status = progress?.status || (isUnlocked ? 'unlocked' : 'locked');
  const stars = progress?.stars || 0;
  const practiceCount = progress?.practiceCount || 0;

  // Status icons and colors
  const statusConfig = {
    locked: {
      icon: '🔒',
      bgClass: 'bg-gray-100',
      borderClass: 'border-gray-200',
      textClass: 'text-gray-400',
    },
    unlocked: {
      icon: '🔓',
      bgClass: 'bg-gradient-to-br from-blue-50 to-indigo-50',
      borderClass: 'border-blue-200',
      textClass: 'text-gray-800',
    },
    'in-progress': {
      icon: '📖',
      bgClass: 'bg-gradient-to-br from-yellow-50 to-orange-50',
      borderClass: 'border-yellow-300',
      textClass: 'text-gray-800',
    },
    completed: {
      icon: '✅',
      bgClass: 'bg-gradient-to-br from-green-50 to-teal-50',
      borderClass: 'border-green-300',
      textClass: 'text-gray-800',
    },
    mastered: {
      icon: '🏆',
      bgClass: 'bg-gradient-to-br from-purple-50 to-pink-50',
      borderClass: 'border-purple-400',
      textClass: 'text-gray-800',
    },
  };

  const config = statusConfig[status];

  return (
    <button
      onClick={onClick}
      disabled={!isUnlocked}
      className={`w-full ${config.bgClass} ${config.borderClass} border-2 rounded-2xl p-4 text-left transition-all ${
        isUnlocked
          ? 'hover:shadow-lg hover:scale-[1.02] cursor-pointer'
          : 'cursor-not-allowed opacity-60'
      } ${isSelected ? 'ring-2 ring-primary ring-offset-2' : ''}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-3">
          <span className="text-2xl">{config.icon}</span>
          <div>
            <p className={`text-lg font-bold ${config.textClass}`}>{rule.pattern}</p>
            <p className="text-sm text-gray-500 line-clamp-1">{rule.description}</p>
          </div>
        </div>
      </div>

      {/* Stars */}
      {isUnlocked && (
        <div className="flex items-center gap-1 mt-3">
          {[1, 2, 3].map(i => (
            <span
              key={i}
              className={`text-lg ${i <= stars ? 'text-yellow-400' : 'text-gray-200'}`}
            >
              ⭐
            </span>
          ))}
          {stars > 0 && (
            <span className="ml-2 text-xs text-gray-500">
              {stars}/3 星
            </span>
          )}
        </div>
      )}

      {/* Sound and practice count */}
      {isUnlocked && (
        <div className="flex items-center justify-between mt-3 text-sm text-gray-500">
          <div className="flex items-center gap-1">
            <span>🔊</span>
            <span className="font-mono">{rule.americanSound}</span>
          </div>
          {practiceCount > 0 && (
            <span>练习 {practiceCount} 次</span>
          )}
        </div>
      )}

      {/* Locked message */}
      {!isUnlocked && (
        <p className="text-sm text-gray-400 mt-2">
          完成前置规则后解锁
        </p>
      )}
    </button>
  );
}
